// Vendors
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';

import { AssetsFileResolver } from 'app-modules/application/containers/AssetsFileResolver';
import { getCookiePolicyUrl } from 'app-modules/application/constants/externalUrls';
import { withNamespaces } from 'react-i18next';

// actual Logo dimensions are 79x16
const PlutoLogo = styled(AssetsFileResolver)`
  margin: 0 auto 15px;
  width: 100px;
  @media (min-width: 960px) {
    width: 150px;
    margin: 0 auto 50px;
  }
`;

// eslint-disable-next-line no-unused-vars
const styles = (theme) => ({
  about: {
    fontFamily: 'Pluto Sans Medium, sans-serif',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 100,
    backgroundColor: '#1f1f1f !important',
    color: '#FFFFFF',
    padding: 24,
    '& a': {
      color: '#A0A0A0',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    display: 'flex',
    alignItems: 'center',
    overflowY: 'auto',
  },
  container: {
    textAlign: 'center',
    height: '100%',
    width: '100%',

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '45% 55%',
      alignItems: 'center',
    },
  },
  image: {
    marginBottom: 20,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 800,
      marginBottom: 0,
    },
    [theme.customBreakpoints.up('lg1')]: {
      width: 1100,
    },
  },
  innerLeft: {
    [theme.breakpoints.up('md')]: {
      overflow: 'hidden',
      justifySelf: 'end',
    },
  },
  innerRight: {
    '& p': {
      fontSize: 18,
    },
    marginBottom: '100px',
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
      transform: 'translateX(-8%)',
    },
  },
  header: {
    fontFamily: 'Pluto Sans Bold, sans-serif',
    fontSize: 30,
    margin: 0,
    marginBottom: 10,
    [theme.breakpoints.up('md')]: {
      fontSize: 40,
    },
    [theme.customBreakpoints.up('lg1')]: {
      fontSize: 45,
    },
  },
  subheader: {
    fontSize: 26,
    margin: 0,
    marginBottom: 30,
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  linkHeader: {
    marginBottom: 8,
    display: 'block',
  },
  buttonLabel: {
    fontSize: 16,
    [theme.breakpoints.up('md')]: {
      fontSize: 18,
    },
  },
  buttonAccept: {
    borderRadius: '25px',
    marginTop: 56,
    marginBottom: 56,
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  leftIcon: {
    marginRight: theme.spacing.unit,
  },
  iconSmall: {
    fontSize: 24,
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
    },
  },
  buttonRoot: {
    marginTop: 20,
    [theme.breakpoints.up('md')]: {
      marginTop: 40,
    },
  },
  desktopAppLink: {
    cursor: 'pointer',
  },

  leftLogo: {
    display: 'block',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  rightLogo: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
});

class PrivacyPolicyUpdateAboutComponent extends PureComponent {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    accept: PropTypes.func.isRequired,
    classes: PropTypes.shape({}).isRequired,
    countryCode: PropTypes.string.isRequired,
    marketingRegion: PropTypes.string.isRequired,
  };

  render() {
    const { show, classes, accept, t, countryCode, marketingRegion } = this.props;
    const userAgent = window.navigator.userAgent;
    const isDesktopApp = userAgent.match(/\PlutoTV\b.*\Electron\b/g);
    const cookiePolicyUrl = getCookiePolicyUrl(marketingRegion);

    if (!show) {
      return null;
    }

    return (
      <section className={classes.about}>
        <div className={classes.container}>
          <div className={classes.innerLeft}>
            <PlutoLogo
              alt="Pluto TV logo"
              path="images/logo-new-colored.svg"
              className={classes.leftLogo}
            />
            <AssetsFileResolver
              pathset={['images/PlutoTV_Devices.png', 'images/PlutoTV_Devices.png']}
              pathsetSizes={['1528w', '768w']}
              className={classes.image}
            />
          </div>

          <div className={classes.innerRight}>
            <PlutoLogo
              alt="Pluto TV logo"
              path="images/logo-new-colored.svg"
              className={classes.rightLogo}
            />
            <h1 className={classes.header}>
              {t('your_privacy_is_important_to_us_short', 'Your privacy is important to us.')}
            </h1>
            <h2 className={classes.subheader}>
              {t('weve_updated_our_terms', "We've updated our terms of use and privacy policy.")}
            </h2>
            <p>
              <span className={classes.linkHeader}>{t('terms_of_use', 'Terms of Use')} </span>
              {isDesktopApp ? (
                <a
                  className={classes.desktopAppLink}
                  onClick={() => window.open('https://corporate.pluto.tv/terms-of-use')}
                >
                  https://corporate.pluto.tv/terms-of-use
                </a>
              ) : (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://corporate.pluto.tv/terms-of-use"
                  >
                    https://corporate.pluto.tv/terms-of-use
                  </a>
                )}
            </p>
            <p>
              <span className={classes.linkHeader}>{t('california_privacy_rights')} </span>
              {isDesktopApp ? (
                <a
                  className={classes.desktopAppLink}
                  onClick={() => window.open('https://corporate.pluto.tv/privacy-policy')}
                >
                  https://corporate.pluto.tv/privacy-policy
                </a>
              ) : (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://corporate.pluto.tv/privacy-policy"
                  >
                    https://corporate.pluto.tv/privacy-policy
                  </a>
                )}
            </p>
            {cookiePolicyUrl ? (
              <p>
                <span className={classes.linkHeader}>{t('cookie_policy')} </span>
                {isDesktopApp ? (
                  <a
                    className={classes.desktopAppLink}
                    onClick={() => window.open(cookiePolicyUrl)}
                  >
                    {cookiePolicyUrl}
                  </a>
                ) : (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={cookiePolicyUrl}
                    >
                      {cookiePolicyUrl}
                    </a>
                  )}
              </p>
            ) : null}
            <p>
              {countryCode === 'us' ? (
                <span className={classes.linkHeader}>
                  {t('do_not_sell_my_personal_information_ccpa')}
                </span>
              ) : (
                  <span className={classes.linkHeader}>{t('do_not_sell_my_personal_info')} </span>
                )}
              {isDesktopApp ? (
                <a
                  className={classes.desktopAppLink}
                  onClick={() => window.open('https://viacomprivacy.com')}
                >
                  https://viacomprivacy.com
                </a>
              ) : (
                  <a target="_blank" rel="noopener noreferrer" href="https://viacomprivacy.com">
                    https://viacomprivacy.com
                  </a>
                )}
            </p>
            <Button
              className={classes.buttonAccept}
              classes={{ label: classes.buttonLabel, root: classes.buttonRoot }}
              variant="contained"
              color="primary"
              size="large"
              onClick={accept}
            >
              <PlayCircleFilledIcon className={classnames([classes.leftIcon, classes.iconSmall])} />
              {t('accept_and_continue')}
            </Button>
          </div>
        </div>
      </section>
    );
  }
}

export const PrivacyPolicyUpdateAbout = withStyles(styles)(
  withNamespaces()(PrivacyPolicyUpdateAboutComponent),
);
